import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, ShoppingCart, AlertCircle, Star } from 'lucide-react';

interface QuantityPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (quantity: number) => void;
  productName: string;
  quantityAvailable: number;
  message: string;
  maxQuantity: number;
}

const QuantityPopup: React.FC<QuantityPopupProps> = ({
  isOpen,
  onClose,
  onConfirm,
  productName,
  quantityAvailable,
  message,
  maxQuantity
}) => {
  const [quantity, setQuantity] = useState(1);

  // Reset quantity when popup opens
  useEffect(() => {
    if (isOpen) {
      setQuantity(1);
    }
  }, [isOpen]);

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= maxQuantity) {
      setQuantity(newQuantity);
    }
  };

  const handleConfirm = () => {
    onConfirm(quantity);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-cosmic-card rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 scale-100">
        {/* Header */}
        <div className="relative p-6 pb-4">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          {/* Fun Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center">
              <Star className="w-8 h-8 text-white" />
            </div>
          </div>
          
          {/* Title */}
          <h2 className="text-2xl font-bold text-white text-center mb-2">
            Limited Stock Alert! 🚨
          </h2>
          
          {/* Product Name */}
          <p className="text-gray-300 text-center text-sm mb-4 line-clamp-2">
            {productName}
          </p>
        </div>

        {/* Message */}
        <div className="px-6 pb-4">
          <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
              <p className="text-yellow-100 text-sm leading-relaxed">
                {message}
              </p>
            </div>
          </div>
        </div>

        {/* Quantity Selector */}
        <div className="px-6 pb-6">
          <div className="bg-gray-800/50 rounded-xl p-4">
            <label className="block text-white font-medium mb-3 text-center">
              How many would you like?
            </label>
            
            {/* Quantity Controls */}
            <div className="flex items-center justify-center gap-4 mb-4">
              <button
                onClick={() => handleQuantityChange(quantity - 1)}
                disabled={quantity <= 1}
                className="w-10 h-10 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 rounded-full flex items-center justify-center transition-colors"
              >
                <Minus className="w-5 h-5" />
              </button>
              
              <div className="bg-cosmic-card border border-primary/30 rounded-xl px-6 py-3 min-w-[80px] text-center">
                <span className="text-2xl font-bold text-white">
                  {quantity}
                </span>
              </div>
              
              <button
                onClick={() => handleQuantityChange(quantity + 1)}
                disabled={quantity >= maxQuantity}
                className="w-10 h-10 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 rounded-full flex items-center justify-center transition-colors"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            
            {/* Quantity Info */}
            <div className="text-center text-sm text-gray-400">
              {quantityAvailable > 1 ? (
                <span>
                  {quantity} of {quantityAvailable} available
                </span>
              ) : (
                <span>Last one available!</span>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 px-4 rounded-xl font-medium transition-colors"
          >
            Maybe Later
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2"
          >
            <ShoppingCart className="w-4 h-4" />
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuantityPopup;
